import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, ArrowRight } from "lucide-react";

// Branded 404 page. Injects a noindex robots meta so crawlers that execute
// JS never index unmatched paths (SPA hosting returns the shell for any URL;
// a true 404 HTTP status requires a hosting-layer rule).
export default function NotFound() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Page Not Found | Holdstill Records";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    // Signal a 404 to prerender/snapshot services that honour it
    const prerender = document.createElement("meta");
    prerender.name = "prerender-status-code";
    prerender.content = "404";
    document.head.appendChild(prerender);
    return () => {
      document.head.removeChild(meta);
      document.head.removeChild(prerender);
      document.title = "Holdstill Records | Music Growth Systems";
    };
  }, []);

  const goToConsult = () => {
    navigate("/");
    setTimeout(() => {
      const el = document.getElementById("consult");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  return (
    <main
      className="min-h-screen bg-[color:var(--hs-bg-0)] text-[color:var(--hs-ink)] flex flex-col items-center justify-center px-4 text-center"
      data-testid="not-found-page"
    >
      <img src="/logo.png" alt="Holdstill Records" className="h-12 w-auto mb-10" />
      <p className="font-mono-ui text-[11px] tracking-[0.35em] uppercase text-[color:var(--hs-lime)]">
        Error 404
      </p>
      <h1 className="mt-4 font-display font-display-rough uppercase text-5xl sm:text-7xl leading-[0.95] text-white">
        This track <span className="text-[color:var(--hs-lime)]">doesn&apos;t exist.</span>
      </h1>
      <p className="mt-5 max-w-md text-sm sm:text-base text-white/70">
        Sorry — the page you&apos;re looking for was moved, renamed or never released.
        Head back to the homepage or book a free consult and we&apos;ll point you the right way.
      </p>
      <div className="mt-9 flex flex-col sm:flex-row items-center gap-3">
        <Button
          asChild
          data-testid="notfound-home-btn"
          className="btn-lift bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl h-12 px-7 text-base"
        >
          <Link to="/">
            <Home className="h-4 w-4 mr-2" /> Back to Home
          </Link>
        </Button>
        <Button
          onClick={goToConsult}
          variant="outline"
          data-testid="notfound-consult-btn"
          className="btn-lift bg-black/30 backdrop-blur-sm border-white/25 text-white hover:border-[color:rgba(202,255,26,0.55)] hover:bg-[color:rgba(202,255,26,0.08)] hover:text-white rounded-xl h-12 px-7 text-base"
        >
          Book a Free Consult <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/40">
        <Link to="/" className="hover:text-[color:var(--hs-lime)]" data-testid="notfound-link-home">
          Home
        </Link>
        <Link to="/privacy" className="hover:text-[color:var(--hs-lime)]" data-testid="notfound-link-privacy">
          Privacy Policy
        </Link>
        <a href="mailto:info@holdstill.in" className="hover:text-[color:var(--hs-lime)]" data-testid="notfound-link-email">
          info@holdstill.in
        </a>
      </div>
    </main>
  );
}
